/**
 * Puerto de voz
 *
 * Abstrae dictado (speech-to-text) y lectura en voz alta (text-to-speech)
 * para permitir diferentes implementaciones:
 * - Web: Web Speech API (SpeechRecognition / speechSynthesis)
 * - Mobile: plugins nativos de Capacitor
 */

/**
 * Callbacks para una sesión de dictado por voz
 */
export interface VoiceInputCallbacks {
  /** Se llama cada vez que cambia el texto transcrito (parcial o final) */
  onTranscriptChange: (text: string) => void

  /** Se llama una única vez cuando la sesión de escucha termina */
  onEnd: () => void

  /** Se llama si ocurre un error durante el reconocimiento */
  onError: (error: unknown) => void
}

export interface VoiceInputPort {
  /** Indica si el dictado por voz está disponible en este entorno */
  isSupported(): boolean

  /** Inicia el dictado en el idioma dado, notificando a través de los callbacks */
  start(lang: string, callbacks: VoiceInputCallbacks): void

  /** Detiene el dictado en curso (dispara onEnd si había una sesión activa) */
  stop(): void
}

export interface VoiceOutputOptions {
  /** Idioma de la voz (por defecto, el que use el motor subyacente) */
  lang?: string

  /** Se llama cuando termina de reproducirse el texto */
  onDone?: () => void

  /** Se llama si ocurre un error durante la síntesis */
  onError?: (error: unknown) => void
}

export interface VoiceOutputPort {
  /** Indica si la síntesis de voz está disponible en este entorno */
  isSupported(): boolean

  /** Reproduce el texto dado en voz alta */
  speak(text: string, options?: VoiceOutputOptions): void

  /** Detiene cualquier reproducción en curso */
  stop(): void
}

/**
 * Variables para almacenar las instancias registradas
 */
let voiceInputInstance: VoiceInputPort | null = null
let voiceOutputInstance: VoiceOutputPort | null = null

/**
 * Registrar la implementación de dictado por voz
 */
export function registerVoiceInput(voiceInput: VoiceInputPort): void {
  voiceInputInstance = voiceInput
}

/**
 * Obtener la instancia de dictado por voz
 * @throws Error si no hay una implementación registrada
 */
export function getVoiceInput(): VoiceInputPort {
  if (!voiceInputInstance) {
    throw new Error('VoiceInputPort no registrado. Llama a registerVoiceInput() primero.')
  }
  return voiceInputInstance
}

/**
 * Verificar si hay una implementación de dictado por voz registrada
 */
export function isVoiceInputRegistered(): boolean {
  return voiceInputInstance !== null
}

/**
 * Reset del dictado por voz (útil para testing)
 */
export function resetVoiceInput(): void {
  voiceInputInstance = null
}

/**
 * Registrar la implementación de síntesis de voz
 */
export function registerVoiceOutput(voiceOutput: VoiceOutputPort): void {
  voiceOutputInstance = voiceOutput
}

/**
 * Obtener la instancia de síntesis de voz
 * @throws Error si no hay una implementación registrada
 */
export function getVoiceOutput(): VoiceOutputPort {
  if (!voiceOutputInstance) {
    throw new Error('VoiceOutputPort no registrado. Llama a registerVoiceOutput() primero.')
  }
  return voiceOutputInstance
}

/**
 * Verificar si hay una implementación de síntesis de voz registrada
 */
export function isVoiceOutputRegistered(): boolean {
  return voiceOutputInstance !== null
}

/**
 * Reset de la síntesis de voz (útil para testing)
 */
export function resetVoiceOutput(): void {
  voiceOutputInstance = null
}
